/**
 * Pulse page data, sourced from the Owners Hub Stats API (`daily_client_activity`
 * + `daily_player_activity`).
 *
 * Returns the same `PulseData` view shape the presentational sections consume (see
 * `mock-data/types.ts`) plus a per-section `loading` map, so the sections are
 * untouched. It replaces the old `mock-data/pulse.ts` (now deleted).
 *
 * WHAT IS DERIVED, AND HOW (field math mirrors the Stats page):
 *  - Business Value = AVERAGE monthly net revenue x 24 months (caption "Valuation
 *    Revenue x Network Multiple", multiple = VALUATION_MONTHS). Net revenue per month =
 *    (casino_ggr_real + sport_ggr_real) - bonus_amount_transformed, over the trailing
 *    complete months (pre-launch all-zero months are trimmed, the current partial month
 *    is excluded). The trend line is the valuation computed cumulatively month by month,
 *    so it ends exactly at the headline figure.
 *  - This month (Revenue / Net Revenue / New Players): the current calendar month to
 *    date, with deltas vs the SAME elapsed span of the prior month (matched baseline).
 *  - Your Asset → Players: the brand's full `daily_player_activity` roster count.
 *  - Your Asset → Revenue per player: this-month GGR / the SAME all-time roster count
 *    shown in the header (so the two figures reconcile), delta vs the matched prior span.
 *  - Your Asset → VIP Contribution: share of total GGR from the top 10% of players by
 *    GGR (a concentration metric over the roster's per-player ggr).
 *  - Your Asset → Players by value (VIP / Regular / Casual / New): objective,
 *    mutually-exclusive tiers from the roster — VIP = the top-10%-by-GGR cohort (same
 *    cohort as VIP Contribution), New = zero activity (no bets, no deposits), Regular =
 *    has deposited, Casual = the rest. Bar shows each tier's share of players.
 *  - Looking ahead = projected annual net at the current pace (trailing-3-month average
 *    net revenue x 12; a sub-month brand's partial month is annualized by run rate).
 *
 * WHAT IS FLAGGED `?` (no backing data — see the page's missing-data note):
 *  - Marketing ROI: no marketing-cost source.
 *  - 30-day Retention: no cohort-retention metric.
 *  - VS. Network (rank / similar-brand medians): no cross-brand benchmark API.
 *  - Looking-ahead tailwind / watch-out cards: no source for the qualitative copy.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchStatsDataSetHistory,
  selectStatsDataSetHistory,
  selectStatsDataSetHistoryError,
  selectStatsDataSetHistoryLoading,
  type StatsHistoryEntry,
  type StatsHistoryRef,
} from '@/store/slices/ownerHubStats';

import type { Player, PulseData, PulseMetric, PulseReachStat, PulseSegment } from '../../mock-data/types';
import type { PulseSegmentId } from '../../theme/tokens';
import { CALIBRATION_WINDOW_DAYS } from '../../utils/catalog';
import { AMOUNT_SCALE } from '../home/stats-mapping';
import {
  type Bucket,
  deltaPct,
  liveBuckets,
  matchedBaseline,
  splitByStart,
  sumBuckets,
  sumFields,
  toBuckets,
} from '../home/stats-shape';
import { usePlayersRoster } from '../players/usePlayersRoster';

// Metallic metric icons (Figma-exported, grey gradient) under /assets/pulse.
const ICON = {
  trend: '/assets/pulse/trend.svg',
  wallet: '/assets/pulse/wallet.svg',
  players: '/assets/pulse/players.svg',
  marketing: '/assets/pulse/marketing.svg',
} as const;

/** The single flat data set every brand-metric reads (no dimensions). */
const DATA_SET = 'daily_client_activity';
/** Micro-units → major currency unit (same factor the rest of Stats uses). */
const MONEY = 1 / AMOUNT_SCALE;
/** Business Value = average monthly net revenue × this many months (the "Network
 *  Multiple" in the caption): a 24-month / 2-year forward valuation. */
const VALUATION_MONTHS = 24;

/** Bucket fields per metric (money totals sum casino_* + sport_*). */
const FIELDS = {
  ggr: ['casino_ggr', 'sport_ggr'],
  ggrReal: ['casino_ggr_real', 'sport_ggr_real'],
  bonus: ['bonus_amount_transformed'],
  registrations: ['registrations'],
  ftds: ['ftds'],
  deposits: ['deposit_amount'],
} as const;

const MS_DAY = 86_400_000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad = (n: number): string => String(n).padStart(2, '0');
/** Local calendar date `YYYY-MM-DD` (the format the multi-day windows use). */
const ymd = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
/** "Jun 2026" from a bucket date (UTC month, matching the Home/Stats month labels). */
const monthLabel = (iso: string): string => {
  const d = new Date(iso);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

const money = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;
const count = (n: number): string => Math.round(n).toLocaleString('en-US');
/** A signed integer-percent pill ("+12%", "-5%"). */
const pctPill = (p: number): string => `${p >= 0 ? '+' : ''}${Math.round(p)}%`;
const mean = (xs: number[]): number => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : 0);
/** Net revenue ($) for one month/day bucket. */
const netOf = (b: Bucket): number => (sumFields(b.metrics, FIELDS.ggrReal) - sumFields(b.metrics, FIELDS.bonus)) * MONEY;

/** Player-value tiers for the "Players by value" bar, in bar order (value desc).
 *  Labels are stable English (the section localizes them via its SEGMENT_LABELS map). */
const VALUE_TIERS: ReadonlyArray<{ id: PulseSegmentId; label: string }> = [
  { id: 'vip', label: 'VIP' },
  { id: 'regular', label: 'Regular' },
  { id: 'casual', label: 'Casual' },
  { id: 'new', label: 'New' },
];

/** Tier counts → one PulseSegment per non-empty tier, whose integer percentages
 *  (share of players) sum to exactly 100 via largest-remainder rounding (each tier
 *  floors, then the leftover points go to the largest fractional remainders). */
const toValueSegments = (counts: Record<PulseSegmentId, number>, total: number): PulseSegment[] => {
  const present = VALUE_TIERS.filter((t) => counts[t.id] > 0).map((t) => {
    const exact = (counts[t.id] / total) * 100;
    const pct = Math.floor(exact);
    return { id: t.id, label: t.label, pct, rem: exact - pct };
  });
  let leftover = 100 - present.reduce((s, t) => s + t.pct, 0);
  [...present]
    .sort((a, b) => b.rem - a.rem)
    .forEach((t) => {
      if (leftover > 0) {
        t.pct += 1;
        leftover -= 1;
      }
    });
  return present.map(({ id, label, pct }) => ({ id, label, pct }));
};

/** The trailing-12-month + current-month-to-date windows, computed once per mount
 *  (stable refs → no refetch loop; a day's staleness is fine for a business overview). */
type Windows = {
  monthlyStart: string;
  monthlyEnd: string;
  dailyStart: string;
  dailyEnd: string;
  /** Split point between the current month and the prior month in the daily call. */
  thisMonthStartMs: number;
  /** Day-of-month today (1-based) — annualizes a sub-month brand's partial month. */
  elapsedDays: number;
};
const computeWindows = (): Windows => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const thisMonthStart = new Date(y, m, 1);
  return {
    // 12 months back → 13 monthly buckets including the current partial month;
    // end-exclusive at next month so the current month is the last bucket.
    monthlyStart: ymd(new Date(y, m - 12, 1)),
    monthlyEnd: ymd(new Date(y, m + 1, 1)),
    // Prior month start → tomorrow (exclusive) so the current day is included; the
    // call carries BOTH this month and the prior month, split client-side.
    dailyStart: ymd(new Date(y, m - 1, 1)),
    dailyEnd: ymd(new Date(y, m, now.getDate() + 1)),
    thisMonthStartMs: Date.parse(ymd(thisMonthStart)),
    elapsedDays: now.getDate(),
  };
};

/** Stale-while-revalidate: skeleton only while there is NO cached history (mirrors
 *  `useStatsMetrics`). First load shows the skeleton; a background refresh keeps the
 *  figures; a first load that failed stops loading (figures fall through to 0); a
 *  missing brand reads as loading. */
const sectionLoading = (
  hasBrand: boolean,
  loadingFlag: boolean,
  entry: StatsHistoryEntry | undefined,
  error: string | undefined,
): boolean => !hasBrand || (entry === undefined && (loadingFlag || error === undefined));

export type PulseDataResult = {
  data: PulseData;
  loading: {
    businessValue: boolean;
    thisMonth: boolean;
    asset: boolean;
    network: boolean;
    lookingAhead: boolean;
  };
  /** True when the page's figures are not yet meaningful: a brand-new brand (under
   *  {@link CALIBRATION_WINDOW_DAYS} days old) or one with no revenue yet, so the page
   *  is covered by the "Calibrating" overlay instead of showing empty/zero figures. */
  calibrating: boolean;
  /** Whole days left in the launch calibration window (>= 1 while the brand is fresh, 0
   *  once past it). Feeds the overlay's "live data in N days" countdown chip. */
  calibrationDaysLeft: number;
};

/**
 * @param casinoInstanceId Active brand id (`displayBrand?.id`); `undefined` keeps the
 *        page in its loading state until a brand resolves.
 */
export const usePulseData = (casinoInstanceId: string | undefined): PulseDataResult => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const w = React.useMemo(computeWindows, []);
  const hasBrand = Boolean(casinoInstanceId);

  // Two history calls: monthly (business value + looking ahead) and daily (this
  // month + reach), plus the full player roster for the player count.
  const monthlyRef: StatsHistoryRef | undefined = casinoInstanceId
    ? { casinoInstanceId, dataSet: DATA_SET, startDate: w.monthlyStart, endDate: w.monthlyEnd, intervalSlice: '1 month' }
    : undefined;
  const dailyRef: StatsHistoryRef | undefined = casinoInstanceId
    ? { casinoInstanceId, dataSet: DATA_SET, startDate: w.dailyStart, endDate: w.dailyEnd, intervalSlice: '1 day' }
    : undefined;

  const monthlyEntry = useAppSelector((s) => selectStatsDataSetHistory(s, monthlyRef));
  const monthlyLoadingFlag = useAppSelector((s) => selectStatsDataSetHistoryLoading(s, monthlyRef));
  const monthlyError = useAppSelector((s) => selectStatsDataSetHistoryError(s, monthlyRef));
  const dailyEntry = useAppSelector((s) => selectStatsDataSetHistory(s, dailyRef));
  const dailyLoadingFlag = useAppSelector((s) => selectStatsDataSetHistoryLoading(s, dailyRef));
  const dailyError = useAppSelector((s) => selectStatsDataSetHistoryError(s, dailyRef));

  const roster = usePlayersRoster(casinoInstanceId);

  // The active brand's launch date, which drives the "brand-new" half of the calibrating
  // gate. Read from `displayBrand` like the figures, so it lags through a brand switch.
  const createdAt = useAppSelector((s) => s.casinoInstance.displayBrand?.created_at);

  React.useEffect(() => {
    if (!casinoInstanceId) return;
    dispatch(fetchStatsDataSetHistory({ casinoInstanceId, dataSet: DATA_SET, startDate: w.monthlyStart, endDate: w.monthlyEnd, intervalSlice: '1 month' }));
    dispatch(fetchStatsDataSetHistory({ casinoInstanceId, dataSet: DATA_SET, startDate: w.dailyStart, endDate: w.dailyEnd, intervalSlice: '1 day' }));
  }, [dispatch, casinoInstanceId, w.monthlyStart, w.monthlyEnd, w.dailyStart, w.dailyEnd]);

  const businessLoading = sectionLoading(hasBrand, monthlyLoadingFlag, monthlyEntry, monthlyError);
  const monthBusyLoading = sectionLoading(hasBrand, dailyLoadingFlag, dailyEntry, dailyError);

  /* ----- Business Value + Looking ahead (monthly history) ----- */
  // Chronological monthly buckets. Trim leading pre-launch all-zero months so the
  // average isn't dragged down by months before the brand existed; the last bucket
  // is the current partial month, excluded from the (complete-month) average.
  const monthBuckets = React.useMemo(() => {
    const all = (monthlyEntry ? toBuckets(monthlyEntry.data) : []).slice().sort((a, b) => Date.parse(a.date) - Date.parse(b.date));
    const firstActive = all.findIndex((b) => sumFields(b.metrics, FIELDS.ggr) > 0 || sumFields(b.metrics, FIELDS.registrations) > 0 || sumFields(b.metrics, FIELDS.deposits) > 0);
    return firstActive === -1 ? [] : all.slice(firstActive);
  }, [monthlyEntry]);

  // Complete months ALWAYS exclude the current partial month (averaging a part-month
  // would understate the valuation). A brand under a month old has none, handled below.
  const completeMonths = monthBuckets.length > 1 ? monthBuckets.slice(0, -1) : [];
  const monthlyNet = completeMonths.map(netOf);

  // A sub-month brand has no complete month yet — estimate its monthly net from the
  // current partial month's run rate, so figures aren't understated mid-month.
  const monthlyRunRate = (): number => {
    if (!monthBuckets.length) return 0;
    const partialNet = netOf(monthBuckets[monthBuckets.length - 1]);
    return (partialNet / Math.max(1, w.elapsedDays)) * (365 / 12);
  };
  const monthlyMean = monthlyNet.length ? mean(monthlyNet) : monthlyRunRate();

  // Business Value = average monthly net revenue × the valuation multiple ("Valuation
  // Revenue × Network Multiple"). The cumulative-mean trend lands exactly on the
  // headline; each point's tooltip is the valuation as of that month.
  const businessValueNum = monthlyMean * VALUATION_MONTHS;
  const series = monthlyNet.map((_, i) => mean(monthlyNet.slice(0, i + 1)) * VALUATION_MONTHS);
  const months = completeMonths.map((b) => monthLabel(b.date));

  /* ----- Calibrating gate ----- */
  // The page is covered by the "Calibrating" overlay while its figures are not yet meaningful,
  // on either of two signals:
  //  - brand-new: the brand launched less than CALIBRATION_WINDOW_DAYS ago (same window that
  //    lifts the Product score on launch); deterministic from `created_at`, so it shows
  //    immediately, even under the first load.
  //  - genuinely empty: the brand has NO data at all yet (no players in the roster, and no
  //    gross revenue / registrations / deposits across the trailing window). An ESTABLISHED
  //    brand (past the launch window) that has any of these has a real dashboard to show even
  //    at $0 GGR, so it is NOT calibrating. Gated on the roster + monthly history having
  //    settled, so a still-loading or erroring brand keeps the normal page, not this overlay.
  const launchedMs = createdAt ? Date.parse(createdAt) : NaN;
  const isFreshBrand = Number.isFinite(launchedMs) && Date.now() - launchedMs < CALIBRATION_WINDOW_DAYS * MS_DAY;
  const allTimeGross = monthBuckets.reduce((s, b) => s + sumFields(b.metrics, FIELDS.ggr), 0);
  const allTimeRegs = monthBuckets.reduce((s, b) => s + sumFields(b.metrics, FIELDS.registrations), 0);
  const allTimeDeposits = monthBuckets.reduce((s, b) => s + sumFields(b.metrics, FIELDS.deposits), 0);
  const rosterSettled = !roster.loading && !roster.error;
  const hasAnyData =
    (rosterSettled && roster.data.length > 0) || allTimeGross > 0 || allTimeRegs > 0 || allTimeDeposits > 0;
  const isEmptyBrand = !isFreshBrand && rosterSettled && monthlyEntry !== undefined && !hasAnyData;
  const calibrating = isFreshBrand || isEmptyBrand;
  // Whole days remaining in the launch window (rounded up, at least 1 while still fresh), for
  // the "your data goes live in N days" chip. 0 when calibrating purely on no-revenue, where
  // there is no time-based countdown to show.
  const calibrationDaysLeft = isFreshBrand
    ? Math.max(1, Math.ceil(CALIBRATION_WINDOW_DAYS - (Date.now() - launchedMs) / MS_DAY))
    : 0;

  // Month-over-month change in the valuation (last two complete months). Hidden (empty)
  // until there are at least two complete months. Copy stays timing-neutral (the trend
  // arrow + sign convey direction).
  let businessDelta = '';
  let businessDeltaDown = false;
  if (series.length >= 2) {
    const cur = series[series.length - 1];
    const prev = series[series.length - 2];
    const diff = cur - prev;
    businessDeltaDown = diff < 0;
    const amount = `${diff >= 0 ? '+' : '-'}${money(Math.abs(diff))}`;
    businessDelta = t('{{amount}} ({{pct}}) last month', { amount, pct: pctPill(deltaPct(cur, prev)) });
  }

  // Looking ahead = projected annual net at the current pace: trailing 3 complete
  // months (or the partial-month run rate) × 12 months.
  const recent = completeMonths.slice(-3);
  const recentMean = recent.length ? mean(recent.map(netOf)) : monthlyRunRate();
  const lookingAheadValue = recentMean * 12;

  /* ----- This month + Revenue per player (daily history, split) ----- */
  const dayBuckets = dailyEntry ? liveBuckets(toBuckets(dailyEntry.data), Date.now()) : [];
  const { current, previous } = splitByStart(dayBuckets, w.thisMonthStartMs);
  const baseline = matchedBaseline(current, previous);

  const grossRaw = sumBuckets(current, FIELDS.ggr);
  const grossBaseRaw = sumBuckets(baseline, FIELDS.ggr);
  const gross = grossRaw * MONEY;
  const net = (sumBuckets(current, FIELDS.ggrReal) - sumBuckets(current, FIELDS.bonus)) * MONEY;
  const regs = sumBuckets(current, FIELDS.registrations);
  const ftds = sumBuckets(current, FIELDS.ftds);

  const margin = gross > 0 ? Math.round((net / gross) * 100) : 0;
  const conv = regs > 0 ? Math.round((ftds / regs) * 100) : 0;

  // Revenue (gross GGR) is the only This-month metric with a period delta; suppress
  // its badge on a fully dead month (a "+0%" up-arrow on $0 reads as fake growth).
  const revenueHasDelta = grossRaw > 0 || grossBaseRaw > 0;
  const revenue: PulseMetric = revenueHasDelta
    ? { icon: ICON.trend, label: 'Revenue', value: money(gross), badge: pctPill(deltaPct(grossRaw, grossBaseRaw)), badgeDown: grossRaw < grossBaseRaw, note: 'vs last' }
    : { icon: ICON.trend, label: 'Revenue', value: money(gross) };

  const thisMonth: PulseMetric[] = [
    revenue,
    { icon: ICON.wallet, label: 'Net Revenue', value: money(net), note: t('{{margin}}% margin', { margin }) },
    { icon: ICON.players, label: 'New Players', value: count(regs), note: t('{{conv}}% to FTD', { conv }) },
    // No marketing-cost source → cannot compute ROI. Flag it.
    { icon: ICON.marketing, label: 'Marketing ROI', value: '', missing: true, note: t('Needs marketing cost data') },
  ];

  /* ----- Your Asset (player base, from the roster) ----- */
  // The roster is now the brand's FULL registration list (every account), enriched by
  // the activity data set — not just the players who transacted. So this is the real
  // player base, and the figures below that divide by it changed denominator with it.
  const rosterCount = roster.data.length;
  // A roster missing its registration half is an UNDER-COUNT, not a smaller brand: it
  // would silently shrink the player base and inflate every per-player figure derived
  // from it. Treat it like the hard error and mark the figures unavailable.
  const rosterUnreliable = roster.error || roster.registrationsFailed;
  // While the roster pages in, show no count (the suffix is dropped); `?` when the
  // count cannot be trusted.
  const playersStr = roster.loading ? '' : rosterUnreliable ? '?' : count(rosterCount);

  // Revenue per player = this-month gross GGR / the SAME all-time roster shown in the
  // header (so the two numbers reconcile). The base is constant, so its delta tracks
  // the revenue delta.
  const revPerPlayer = rosterCount > 0 ? gross / rosterCount : 0;
  const revPerPlayerBase = rosterCount > 0 ? (grossBaseRaw * MONEY) / rosterCount : 0;
  const rppPct = deltaPct(revPerPlayer, revPerPlayerBase);

  // VIP Contribution = share of total operator GGR generated by the top 10% of ACTIVE
  // players by GGR — a standard concentration metric derived from the roster's
  // per-player ggr (no external value-tier dimension needed). Falls back to `?` when
  // GGR is non-positive (operator net-losing / no activity), where the ratio is
  // meaningless.
  //
  // The top 10% is taken over players who actually transacted, NOT over the whole
  // registration list, which is what the roster became. On a real brand the vast
  // majority of registrations never play (verified: 217 registered, 5 with activity),
  // so 10% of the full list is wider than the entire earning population and the ratio
  // collapses to a constant 100% — true, and useless as a concentration measure.
  const activePlayers = roster.data.filter((p) => p.hasActivity);
  const playersByGgr = activePlayers.slice().sort((a, b) => b.ggr - a.ggr);
  const ggrDesc = playersByGgr.map((p) => p.ggr);
  const totalGgr = ggrDesc.reduce((s, v) => s + v, 0);
  const vipCount = Math.max(1, Math.ceil(ggrDesc.length * 0.1));
  const vipGgr = ggrDesc.slice(0, vipCount).reduce((s, v) => s + v, 0);
  // Clamp to 100%: when the rest of the base is net-negative the raw ratio can exceed
  // 100, which reads as a bug rather than "VIPs carry the brand".
  const vipContribution = ggrDesc.length > 0 && totalGgr > 0 ? Math.min(100, Math.round((vipGgr / totalGgr) * 100)) : null;

  // Players by value — objective, mutually-exclusive tiers from the roster (no invented
  // money cut-offs), assigned by precedence:
  //   VIP     = the SAME top-10%-by-GGR cohort as VIP Contribution, limited to players
  //             who actually generated GGR (a 0-revenue brand has no VIPs).
  //   New     = registered with zero activity (no bets and no deposits).
  //   Regular = has deposited (a paying customer).
  //   Casual  = everyone else (placed bets but never deposited).
  const vipIds = new Set(
    playersByGgr
      .slice(0, vipCount)
      .filter((p) => p.ggr > 0)
      .map((p) => p.id),
  );
  const tierOf = (p: Player): PulseSegmentId => {
    if (vipIds.has(p.id)) return 'vip';
    if (p.bets === 0 && p.depositsCount === 0) return 'new';
    if (p.depositsCount > 0) return 'regular';
    return 'casual';
  };
  const tierCounts: Record<PulseSegmentId, number> = { vip: 0, regular: 0, casual: 0, new: 0 };
  roster.data.forEach((p) => {
    tierCounts[tierOf(p)] += 1;
  });
  // An empty / still-paging / erroring roster keeps the bar's "not available yet"
  // state. So does a roster whose activity half failed: every tier below reads off the
  // money figures, so the whole base would land in `new` and the bar would report
  // "100% New" as a measured fact about players it simply has no figures for.
  const tiersMeasurable = rosterCount > 0 && !rosterUnreliable && !roster.activityFailed;
  const valueSegments = tiersMeasurable ? toValueSegments(tierCounts, rosterCount) : [];

  const reach: PulseReachStat[] = [
    // No cohort-retention metric in the data set.
    { label: '30-day Retention', value: '', missing: true },
    // Per-player revenue divides by the roster, so an under-counted base inflates it.
    rosterUnreliable
      ? { label: 'Revenue per player', value: '', missing: true }
      : { label: 'Revenue per player', value: money(revPerPlayer), delta: pctPill(rppPct), down: rppPct < 0 },
    vipContribution === null
      ? { label: 'VIP Contribution', value: '', missing: true }
      : { label: 'VIP Contribution', value: `${vipContribution}%` },
  ];

  const data: PulseData = {
    businessValue: {
      value: businessValueNum,
      delta: businessDelta,
      deltaDown: businessDeltaDown,
      // Plain stable-English caption — the section localizes it via its SUB_LABELS map.
      sub: 'Valuation Revenue x Network Multiple',
      series,
      months,
    },
    thisMonth,
    asset: {
      players: playersStr,
      segments: valueSegments,
      segmentsMissing: valueSegments.length === 0,
      reach,
    },
    network: { brands: 0, rank: 0, top: '', rows: [], missing: true },
    lookingAhead: {
      value: money(lookingAheadValue),
      span: '12 Months Trajectory',
      // Plain stable-English caption — the section localizes it via its SUB_LABELS map.
      sub: 'Projected annual Net if current pace holds',
      items: [],
      itemsMissing: true,
    },
  };

  return {
    data,
    loading: {
      businessValue: businessLoading,
      thisMonth: monthBusyLoading,
      asset: monthBusyLoading || roster.loading,
      network: false,
      lookingAhead: businessLoading,
    },
    calibrating,
    calibrationDaysLeft,
  };
};

export default usePulseData;
